import React from 'react';
import QueryParams, { isInQueryParamsArray } from 'context/QueryParams';
import {
  fetchSettings,
  setSettings,
  fetchFilters,
  getPermissions,
  getStoredQuery,
} from 'utils/api';
import {
  IFilter,
  ISettings,
  ISettingsResponse,
  IFilterResponse,
  ISpendByType,
  IBreakdownByResponse,
  ICurrencyFormat,
  ISettingItem,
  ICurrency,
  IDecimal,
  IUnit,
  IAnalysisProps,
  IDisplayByType,
} from 'utils/types';
import { convertStoredQueryToParams } from 'utils/storedQuery';
import {
  DEFAULT_CURRENCY_FORMAT,
  FILTER_LABELS,
  PERMISSIONS,
  L1Suffix,
  L2Suffix,
} from 'utils/constants';

interface IAnalysisState {
  breakdownBy?: IBreakdownByResponse;
  spendBy: ISpendByType;
  filters: IFilter[];
  permissions: string[];
  settings?: ISettingsResponse;
  isPermissionsLoading: boolean;
  storedQuery: { [key: string]: string | string[] } | null;
}

export const ApiContext = React.createContext<{
  getSettings: () => Promise<any>;
  setSettings: (settings: ISettings) => Promise<any>;
  downloadSpendData: (o: { displayBy: IDisplayByType }) => Promise<any>;
  downloadSpendYoYData: () => Promise<any>;
  downloadSpendTopVendorsData: () => Promise<any>;
  downloadSpendBreakdownChartData: (d: string) => Promise<any>;
  downloadSpendHeadlineData: () => Promise<any>;
  downloadSpendHeadcountData: (s: string) => Promise<any>;
  downloadSpendHeadcountTableData: (s: string) => Promise<any>;
  downloadSpendBreakdownBusinessUnitData: (s: string) => Promise<any>;
  downloadSpendGeographyData: (s: string) => Promise<any>;
  downloadSpendTopThirdPartyData: () => Promise<any>;
  fetchHeadlines: () => Promise<any>;
  format: ICurrencyFormat;
  filters: IFilter[];
  isPermissionsLoading: boolean;
  permissions: string[];
  settings: ISettingsResponse;
  spendBy: ISpendByType;
  updateAppState: (p: any) => void;
  storedQuery: { [key: string]: string | string[] } | null;
}>({} as any);

class Api extends React.Component<IAnalysisProps, IAnalysisState> {
  state = {
    filters: [],
    permissions: [] as IAnalysisState['permissions'],
    settings: undefined,
    spendBy: ISpendByType.SERVICE_TOWER,
    isPermissionsLoading: true,
    storedQuery: null,
  };

  getSettings = () => {
    return fetchSettings().then((settingsResponse: ISettingsResponse) => {
      this.setState({ settings: settingsResponse });
    });
  };

  updateSettings = (settings: ISettings) => {
    return setSettings(settings);
  };

  fetchStoredQuery = async () => {
    const { params: queryParams, setParams } = this.props;

    return getStoredQuery(queryParams.storedQueryId as string)
      .then((items) => {
        this.setState({ storedQuery: convertStoredQueryToParams(items) });
      })
      .catch(() => {
        setParams({ storedQueryId: undefined });
        localStorage.clear();
      });
  };

  getFilters = async () => {
    const { params: queryParams } = this.props;
    const { storedQuery } = this.state;
    const selectedFilters = storedQuery || queryParams;

    return fetchFilters().then((filtersResponse: IFilterResponse) => {
      this.setState({
        filters: Object.keys(filtersResponse)
          .filter((filterName: string) => filterName !== 'contributions')
          .map((filterName: string) => {
            return {
              name: FILTER_LABELS[filterName],
              id: filterName,
              options: filtersResponse[filterName].map((item: any) => ({
                id: item.id.toString(),
                label: item.value,
                options:
                  item.options.length > 0
                    ? item.options?.map(({ value, id }: any) => ({
                        label: value,
                        id: id.toString(),
                        isSelected:
                          isInQueryParamsArray(
                            {
                              [`${filterName}${L1Suffix}`]: item.id.toString(),
                            },
                            selectedFilters
                          ) ||
                          isInQueryParamsArray(
                            {
                              [`${filterName}${L2Suffix}`]: id.toString(),
                            },
                            selectedFilters
                          ),
                      }))
                    : undefined,
                isSelected:
                  isInQueryParamsArray(
                    {
                      [filterName]: item.value,
                    },
                    selectedFilters
                  ) ||
                  isInQueryParamsArray(
                    {
                      [`${filterName}${L1Suffix}`]: item.id.toString(),
                    },
                    selectedFilters
                  ),
              })),
            };
          }),
      });
    });
  };

  verifySelectedFilters = () => {
    const { params: queryParams } = this.props;
    const { storedQuery } = this.state;
    const selectedFilters = storedQuery || queryParams;

    const { filters } = this.state;
    return filters.map((filter: IFilter) => ({
      ...filter,
      options: filter.options.map((parent: any) => ({
        ...parent,
        isSelected:
          isInQueryParamsArray(
            { [filter.id]: parent.label },
            selectedFilters
          ) ||
          isInQueryParamsArray(
            {
              [`${filter.id}${L1Suffix}`]: parent.id.toString(),
            },
            selectedFilters
          ),
        options:
          parent.options?.length > 0
            ? parent.options.map((item: any) => ({
                ...item,
                isSelected:
                  isInQueryParamsArray(
                    {
                      [`${filter.id}${L1Suffix}`]: parent.id.toString(),
                    },
                    selectedFilters
                  ) ||
                  isInQueryParamsArray(
                    {
                      [`${filter.id}${L2Suffix}`]: item.id.toString(),
                    },
                    selectedFilters
                  ),
              }))
            : undefined,
      })),
    }));
  };

  getSelectedCurrency = (settingArray: ISettingItem[]) => {
    const item = settingArray.find((item) => item.default === true);
    if (item) {
      return item.value as ICurrency;
    }
    return DEFAULT_CURRENCY_FORMAT.currency;
  };
  getSelectedDecimal = (settingArray: ISettingItem[]) => {
    const item = settingArray.find((item) => item.default === true);
    if (item) {
      return item.value as IDecimal;
    }
    return DEFAULT_CURRENCY_FORMAT.decimal;
  };
  getSelectedUnit = (settingArray: ISettingItem[]) => {
    const item = settingArray.find((item) => item.default === true);
    if (item) {
      return item.value as IUnit;
    }
    return DEFAULT_CURRENCY_FORMAT.unit;
  };

  findSelectedSettingValue = (settingArray: ISettingItem[]) => {
    const item = settingArray.find((item) => item.default === true);
    if (item) {
      return item.value;
    }
    return settingArray[0] ? settingArray[0].value : '';
  };

  getCurrencyFormat = () => {
    const { settings }: { settings?: ISettingsResponse } = this.state;
    if (settings) {
      const { currencies, decimals, units } = settings;
      return {
        currency: this.getSelectedCurrency(currencies),
        decimal: this.getSelectedDecimal(decimals),
        unit: this.getSelectedUnit(units),
      };
    }
    return DEFAULT_CURRENCY_FORMAT;
  };

  getPermissions = () => {
    return getPermissions().then((permissions: string[]) => {
      this.setState({
        isPermissionsLoading: false,
        permissions,
      });
    });
  };

  updateAppState = (state: IAnalysisState, callback?: () => void) => {
    this.setState({ ...state }, () => {
      if (callback) {
        callback();
      }
    });
  };

  componentDidUpdate(prevProps: IAnalysisProps) {
    const { params } = this.props;

    if (prevProps.params.storedQueryId !== params.storedQueryId) {
      if (params.storedQueryId) {
        this.fetchStoredQuery();
      } else {
        this.setState({ storedQuery: null });
      }
    }
  }

  componentDidMount() {
    this.getAppData();
  }

  getAppData = () => {
    this.getPermissions().then(async () => {
      const { permissions } = this.state;
      const hasSpendViewPermission = permissions.includes(
        PERMISSIONS.SPEND_ANALYSIS_READ
      );
      const hasSettingsPermission = permissions.includes(
        PERMISSIONS.PREFERENCES_READ
      );

      if (hasSpendViewPermission) {
        const { params } = this.props;

        if (params.storedQueryId) {
          await this.fetchStoredQuery();
        }

        this.getFilters();
      }

      if (hasSettingsPermission) {
        this.getSettings();
      }
    });
  };

  render() {
    const { isPermissionsLoading } = this.state;

    return (
      <ApiContext.Provider
        // @ts-ignore
        value={{
          ...this.state,
          format: this.getCurrencyFormat(),
          filters: this.verifySelectedFilters(),
          getSettings: this.getSettings,
          setSettings: this.updateSettings,
          updateAppState: this.updateAppState,
        }}
      >
        {!isPermissionsLoading ? this.props.children : null}
      </ApiContext.Provider>
    );
  }
}

export default ({ children }: { children: React.ReactNode }) => (
  <QueryParams>
    {({ params, setParams }) => (
      <Api params={params} setParams={setParams}>
        {children}
      </Api>
    )}
  </QueryParams>
);
